import {Deck, _GlobeView as GlobeView, _GlobeController as GlobeController, _GlobeViewport as GlobeViewport} from '@deck.gl/core';
import {TileLayer} from '@deck.gl/geo-layers';
import {BitmapLayer, PathLayer, ScatterplotLayer, TextLayer} from '@deck.gl/layers';
import {
  buildQuadtreeIndex,
  computeGlobeCullMinDot,
  computeGlobeScreenBBox
} from './quadtree.js';
import {TileLoadScheduler} from './tile-load-scheduler.js';
import {TileStore, FILL_VALUE} from './tile-store.js';
import {analyzeSeamTile, isSeamDebugEnabled} from './seam-debug.js';
import MeshTileLayer from './mesh-tile-layer.js';
import {mergeMeshTiles} from './mesh-merge.js';
import {ParticleAnimation} from './particle-animation.js';
import {tileDiag, diagNow} from './tile-load-diagnostics.js';
import {setGeometryContentRevision} from './geometry-cache.js';
import {
  loadStationCatalog,
  loadSuccessfulStationIds,
  loadStationSeries,
  renderStationHydrograph,
  stationRecordsToCsvRows
} from './station-hydrograph.js';
import {COLORMAPS, DEFAULT_COLORMAP_ID, getColormap} from './colormaps.js';
import {
  loadOfclStormsForCycle,
  positionsAtManifestTime,
  createOfclLayers
} from './ofcl-storms.js';
import {initRenderAnimation, viewStateForBbox} from './render-animation.js';
import {initTrackParticles} from './track-particles.js';
import {initAnomalyDetection} from './anomaly-detection.js';

// deck.gl 9.x GlobeController zooms to view center only. Anchor mode keeps the
// lon/lat under the cursor fixed (same idea as MapState.zoom + WebMercator).
// Basemap tiles and GlobeViewport.unproject() break when the pole fills the
// screen. Keep the view latitude inside this band (degrees from equator).
const POLE_VIEW_LATITUDE_LIMIT = 70;

function zoomAdjust(latitude) {
  return Math.log2(Math.PI * Math.cos((latitude * Math.PI) / 180));
}

function clampViewLatitude(latitude) {
  return Math.max(Math.min(latitude, POLE_VIEW_LATITUDE_LIMIT), -POLE_VIEW_LATITUDE_LIMIT);
}

function globePanByAnchor(viewport, lngLat, pixel) {
  if (!lngLat || !pixel || pixel.length < 2) {
    return {longitude: viewport.longitude, latitude: viewport.latitude};
  }
  const underCursor = viewport.unproject(pixel);
  if (!underCursor || underCursor.length < 2) {
    return {longitude: viewport.longitude, latitude: viewport.latitude};
  }
  return {
    longitude: viewport.longitude + (lngLat[0] - underCursor[0]),
    latitude: clampViewLatitude(viewport.latitude + (lngLat[1] - underCursor[1]))
  };
}

class ZoomToCursorGlobeController extends GlobeController {
  constructor(props) {
    super(props);
    const BaseGlobeState = this.ControllerState;
    class ZoomToCursorGlobeState extends BaseGlobeState {
      applyConstraints(props) {
        const constrained = super.applyConstraints(props);
        const lat = constrained.latitude;
        const clamped = clampViewLatitude(lat);
        if (clamped !== lat) {
          constrained.zoom += zoomAdjust(clamped) - zoomAdjust(lat);
          constrained.latitude = clamped;
        }
        return constrained;
      }

      zoom({pos, startPos, scale}) {
        // Controller wheel zoom passes {pos, scale} only — not startPos.
        const anchorPos = startPos || pos;

        let {startZoom, startZoomLngLat} = this.getState();
        if (!startZoomLngLat) {
          startZoom = this.getViewportProps().zoom;
          if (anchorPos) {
            startZoomLngLat = this._unproject(anchorPos);
          }
        }

        const zoom = this._constrainZoom(startZoom + Math.log2(scale));
        if (!startZoomLngLat || !anchorPos) {
          return this._getUpdatedState({zoom});
        }

        const zoomedViewport = this.makeViewport({...this.getViewportProps(), zoom});
        const panProps = globePanByAnchor(zoomedViewport, startZoomLngLat, anchorPos);

        return this._getUpdatedState({zoom, ...panProps});
      }
    }
    this.ControllerState = ZoomToCursorGlobeState;
  }
}

// deck.gl 9.3.6 switches to flat WebMercatorViewport at zoom > 12. That
// breaks TileLayer draping over the sphere and makes pole views go black.
class PoleSafeGlobeView extends GlobeView {
  getViewportType() {
    return GlobeViewport;
  }
}

// Fallback used only if /chl_data/tiles_active.json can't be loaded (e.g.
// local testing against a single chunker run with no forecast-cycle
// structure). Once a cycle is selected this is replaced by
// `/chl_data/<cycle>`, e.g. `/chl_data/2026-07-07_12`.
let TILES_BASE_URL = '/tiles_adaptive';

const ACTIVE_CYCLES_URL = '/chl_data/tiles_active.json';

// Fill-layer display modes. `vectorField` is which vector NetCDF field
// (if any) the magnitude is derived from; null means use the zeta
// scalar (ensureScalar) directly, matching the original "Water Level"
// behavior. `derived: 'elementSize'` is geometry-only (circumdiameter in
// km, once per tile). `derived: 'elementShape'` is also geometry-only:
// equilateral quality q = 4√3·Area/(a²+b²+c²) ∈ (0,1]. `derived: 'depth'`
// is the static bathymetry from depth.bin (fetched once per tile, not per
// timestep). Domain is what the jet colormap's [0,1] range is stretched
// across -- Water Level is diverging (centered on 0), the speed /
// element-size / shape / depth fields are magnitudes (always >= 0).
const DISPLAY_FIELDS = {
  waterlevel: {label: 'Water Level (m)', domain: [-1.5, 1.5], vectorField: null},
  waterspeed: {label: 'Depth-Averaged Water Velocity (m/s)', domain: [0, 1.0], vectorField: 'velocity'},
  windspeed: {label: '10 m Wind Velocity (m/s)', domain: [0, 25.0], vectorField: 'wind'},
  depth: {
    label: 'Depth below geoid (m)',
    // Symmetric so Topo–Bathy white (midpoint) lands on sea level;
    // ADCIRC depth is + below geoid, − on land.
    domain: [-30, 30],
    vectorField: null,
    derived: 'depth',
    defaultColormap: 'topo_bathy'
  },
  elementsize: {
    label: 'Element Size (km)',
    domain: [0, 25],
    vectorField: null,
    derived: 'elementSize'
  },
  elementshape: {
    label: 'Element Shape (0–1)',
    domain: [0.9, 1.0],
    vectorField: null,
    derived: 'elementShape'
  }
};

const statusEl = document.getElementById('status');
const timestepBigInput = document.getElementById('timestepBig');
const timestepTicksList = document.getElementById('timestepTicks');
const timestepLabelsEl = document.getElementById('timestepLabels');
const basemapSelect = document.getElementById('basemapSelect');
const opacitySlider = document.getElementById('opacitySlider');
const opacityValueEl = document.getElementById('opacityValue');
const legendBarEl = document.querySelector('#legend .legend-bar');
const showEdgesToggle = document.getElementById('showEdgesToggle');
const hydrographModal = document.getElementById('hydrographModal');
const hydrographTitle = document.getElementById('hydrographTitle');
const hydrographSubtitle = document.getElementById('hydrographSubtitle');
const hydrographClose = document.getElementById('hydrographClose');
const hydrographDownloadCsv = document.getElementById('hydrographDownloadCsv');
const hydrographStatus = document.getElementById('hydrographStatus');
const hydrographPlot = document.getElementById('hydrographPlot');
const hydrographLegend = document.getElementById('hydrographLegend');
const vectorFieldSelect = document.getElementById('vectorFieldSelect');
const particleCanvas = document.getElementById('particleCanvas');
const displayFieldSelect = document.getElementById('displayFieldSelect');
const legendTitleEl = document.getElementById('legendTitle');
const legendTicksEl = document.getElementById('legendTicks');
const showDiagnosticsToggle = document.getElementById('showDiagnosticsToggle');
const showOfclToggle = document.getElementById('showOfclToggle');
const forecastCycleSelect = document.getElementById('forecastCycleSelect');
const legendMinInput = document.getElementById('legendMinInput');
const legendMaxInput = document.getElementById('legendMaxInput');
const colormapSelect = document.getElementById('colormapSelect');

// Native <select> dropdown height can't be capped with CSS. When a menu has
// more than seven options, expand it in-place to seven visible rows and scroll.
const SELECT_VISIBLE_ROWS = 7;

function collapseScrollableSelect(select) {
  select.size = 1;
  select.classList.remove('select-scrollable');
}

function expandScrollableSelect(select) {
  if (select.options.length <= SELECT_VISIBLE_ROWS) {
    collapseScrollableSelect(select);
    return;
  }
  select.size = SELECT_VISIBLE_ROWS;
  select.classList.add('select-scrollable');
}

function wireScrollableSelects(root = document) {
  for (const select of root.querySelectorAll('#optionsPanel select')) {
    if (select.dataset.scrollWired === '1') continue;
    select.dataset.scrollWired = '1';
    select.addEventListener('focus', () => expandScrollableSelect(select));
    select.addEventListener('blur', () => collapseScrollableSelect(select));
    select.addEventListener('change', () => {
      collapseScrollableSelect(select);
      select.blur();
    });
  }
}

const BASEMAP_STYLES = {
  osm: {
    data: 'https://tile.openstreetmap.org/{z}/{x}/{y}.png',
    maxZoom: 19
  },
  satellite: {
    // Esri World Imagery -- free, no API key required.
    data: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
    maxZoom: 19
  }
};

let manifest = null;
let index = null;
let store = null;
let timestep = 0;
let deckInstance = null;
let lastVisible = []; // [z, x, y][] for the current viewport, set by refresh()
let basemapStyle = 'osm';
let dataOpacity = 1; // 0-1, applied to mesh tiles and the colorbar legend
let showEdges = false; // off by default -- see mesh-tile-layer.js for why this is cheap either way
let activeVectorField = null; // null | 'velocity' | 'wind' -- drives the particle animation overlay
let displayField = 'waterlevel'; // drives the fill layer; see DISPLAY_FIELDS keys
let colormapId = DEFAULT_COLORMAP_ID;
let particleAnim = null;
// Effective legend/color domain for the current display field. Starts as
// a copy of DISPLAY_FIELDS[displayField].domain (its "default") every
// time the field changes, and diverges from it only if the user edits
// the Min/Max Legend Value inputs -- see resetLegendDomainInputs().
let domainMin = DISPLAY_FIELDS.waterlevel.domain[0];
let domainMax = DISPLAY_FIELDS.waterlevel.domain[1];

let stationById = new Map();
let cycleStationIds = [];
let selectedStationId = null;
let hydrographRequestId = 0;
let hydrographCsvDownload = null; // {filename, rows}

let showOfcl = false;
let showStations = true; // hidden during Render Animation capture
let ofclStorms = []; // NHC tracks for the current forecast cycle
let ofclLoadId = 0;
let trackMarkers = []; // {id, lon, lat}[]
let trackResult = null; // {paths:[{id,path,color}], ...}
let trackPlaceMode = false; // suppress hydrograph clicks while placing
let anomalyMarkers = []; // {id, lon, lat, focused?}[]
let anomalySelectMode = false; // suppress hydrograph; mesh node picking
let anomalyApi = null; // handleMapClick / isSelecting / hasResult
let activeCyclesList = []; // from tiles_active.json (sorted desc)

// NHC vmax is 1-min sustained (kt). Convert to approx 10-min mean m/s.
const KT_TO_MS = 0.514444;
const NHC_1MIN_TO_10MIN = 0.9;

const stormHoverTipEl = document.getElementById('stormHoverTip');
const ofclTileWindCache = new Map(); // key -> number | null | 'pending'

function nhcTenMinWindMs(vmaxKt) {
  if (vmaxKt == null || !Number.isFinite(Number(vmaxKt))) return null;
  return Number(vmaxKt) * KT_TO_MS * NHC_1MIN_TO_10MIN;
}

function formatMs(v) {
  if (v == null || !Number.isFinite(v)) return 'n/a';
  return `${v.toFixed(1)} m/s`;
}

function sampleCachedTileWindMs(lon, lat) {
  const nearest = findNearestVertex(lon, lat);
  if (!nearest) return {value: null, status: 'no-mesh'};
  const entry = store.get(nearest.z, nearest.x, nearest.y);
  if (!entry || entry.status !== 'geometry-ready') return {value: null, status: 'no-mesh', nearest};

  const speedMap = entry.windSpeedByTimestep;
  if (speedMap?.has(timestep)) {
    const v = speedMap.get(timestep).scalar[nearest.vertexIndex];
    if (v === FILL_VALUE || v == null || !Number.isFinite(v)) {
      return {value: null, status: 'dry', nearest};
    }
    return {value: v, status: 'ok', nearest};
  }

  const uv = entry.windByTimestep?.get(timestep);
  if (uv) {
    const u = uv[nearest.vertexIndex * 2];
    const v = uv[nearest.vertexIndex * 2 + 1];
    if (u === FILL_VALUE || v === FILL_VALUE || !Number.isFinite(u) || !Number.isFinite(v)) {
      return {value: null, status: 'dry', nearest};
    }
    return {value: Math.hypot(u, v), status: 'ok', nearest};
  }

  return {value: null, status: 'missing', nearest};
}

function requestTileWindForStorm(pos) {
  const key = `${pos.id}|${timestep}|${pos.lon.toFixed(2)}|${pos.lat.toFixed(2)}`;
  if (ofclTileWindCache.has(key)) return ofclTileWindCache.get(key);

  const sampled = sampleCachedTileWindMs(pos.lon, pos.lat);
  if (sampled.status === 'ok' || sampled.status === 'dry' || sampled.status === 'no-mesh') {
    ofclTileWindCache.set(key, sampled);
    return sampled;
  }

  ofclTileWindCache.set(key, {value: null, status: 'pending'});
  const nearest = sampled.nearest || findNearestVertex(pos.lon, pos.lat);
  if (!nearest) {
    const miss = {value: null, status: 'no-mesh'};
    ofclTileWindCache.set(key, miss);
    return miss;
  }
  store
    .ensureSpeed(nearest.z, nearest.x, nearest.y, timestep, 'wind')
    .then(() => {
      const again = sampleCachedTileWindMs(pos.lon, pos.lat);
      ofclTileWindCache.set(key, again);
      // Do not call renderVisible() here — it can thrash the mesh layer stack.
    })
    .catch(() => {
      ofclTileWindCache.set(key, {value: null, status: 'error'});
    });
  return {value: null, status: 'pending'};
}

function renderStormHoverTipContent(pos, tileWind) {
  const label = pos.label || stormLabelFallback(pos);
  const nhcMs = nhcTenMinWindMs(pos.vmax);
  let tileLine;
  if (tileWind.status === 'pending') tileLine = 'Tile wind: loading…';
  else if (tileWind.status === 'no-mesh') tileLine = 'Tile wind: n/a (no mesh)';
  else if (tileWind.status === 'dry') tileLine = 'Tile wind: dry / missing';
  else if (tileWind.status === 'error') tileLine = 'Tile wind: unavailable';
  else tileLine = `Tile wind: ${formatMs(tileWind.value)}`;

  stormHoverTipEl.innerHTML =
    `<strong>${label}</strong>` +
    `NHC 10-min wind: ${formatMs(nhcMs)}` +
    (pos.vmax != null
      ? ` <span style="color:#666">(${Math.round(pos.vmax)} kt → ×0.9)</span>`
      : '') +
    `<br>${tileLine}`;
}

function updateStormHoverTip(info) {
  if (!stormHoverTipEl) return;
  const layerId = info?.layer?.id;
  const obj = info?.object;
  if (!showOfcl || !obj || (layerId !== 'ofcl-positions' && layerId !== 'ofcl-tracks')) {
    stormHoverTipEl.classList.add('hidden');
    delete stormHoverTipEl.dataset.stormKey;
    return;
  }
  // Prefer marker position object (has lon/lat/vmax); tracks only have path.
  const pos = layerId === 'ofcl-positions'
    ? obj
    : (getOfclPositions().find(p => p.id === obj.id) || null);
  if (!pos || pos.lon == null || pos.lat == null) {
    stormHoverTipEl.classList.add('hidden');
    return;
  }
  const key = `${pos.id}|${timestep}|${pos.lon.toFixed(2)}|${pos.lat.toFixed(2)}`;
  stormHoverTipEl.dataset.stormKey = key;
  const tileWind = requestTileWindForStorm(pos);
  renderStormHoverTipContent(pos, tileWind);
  stormHoverTipEl.classList.remove('hidden');
  const x = info.x ?? 0;
  const y = info.y ?? 0;
  stormHoverTipEl.style.left = `${x + 14}px`;
  stormHoverTipEl.style.top = `${y + 14}px`;
}

async function refreshOfclStorms() {
  const cycle = getCurrentForecastCycle();
  const loadId = ++ofclLoadId;
  ofclStorms = [];
  ofclTileWindCache.clear();
  if (!showOfcl || !cycle) {
    if (deckInstance) renderVisible();
    return;
  }
  try {
    const storms = await loadOfclStormsForCycle(cycle);
    if (loadId !== ofclLoadId) return;
    ofclStorms = storms;
    if (window.__debug) window.__debug.ofclStorms = storms;
    console.info(
      `[ofcl] cycle ${cycle}: ${storms.length} storm(s)`,
      storms.map(s => `${s.id}@${s.initDtg}`).join(', ') || '(none)'
    );
  } catch (err) {
    if (loadId !== ofclLoadId) return;
    console.warn('[ofcl] failed to load NHC forecast tracks', err);
    ofclStorms = [];
  }
  if (deckInstance) renderVisible();
}

function getOfclPositions() {
  if (!showOfcl || !ofclStorms.length || !manifest?.timesteps) return [];
  return positionsAtManifestTime(ofclStorms, manifest.timesteps[timestep]);
}

function stormLabelFallback(object) {
  if (object.label) return object.label;
  const id = object.id || `${object.basin || ''}${object.cy || ''}`;
  return object.name ? `${object.name} (${id})` : id;
}

function setStatus(text) {
  statusEl.textContent = text;
}

// Legend ticks + gradient bar. The bar CSS comes from COLORMAPS so it
// tracks the shader's applyColormap() choice.
function updateLegend() {
  const {label} = DISPLAY_FIELDS[displayField];
  legendTitleEl.textContent = label;
  const cmap = getColormap(colormapId);
  legendBarEl.style.background = cmap.css;
  const dmin = domainMin;
  const dmax = domainMax;
  const ticks = legendTicksEl.children;
  const n = ticks.length;
  for (let i = 0; i < n; i++) {
    // ticks run top (max) to bottom (min), matching the "to top" gradient
    const frac = i / (n - 1);
    const value = dmax - frac * (dmax - dmin);
    ticks[i].textContent = value.toFixed(1);
  }
}

// Resets Min/Max Legend Value back to DISPLAY_FIELDS[displayField]'s
// default domain and reflects that in the number inputs. Called whenever
// the display field changes -- a per-field custom domain the user typed
// in doesn't carry over to a different field's defaults.
function resetLegendDomainInputs() {
  const [dmin, dmax] = DISPLAY_FIELDS[displayField].domain;
  domainMin = dmin;
  domainMax = dmax;
  legendMinInput.value = String(dmin);
  legendMaxInput.value = String(dmax);
}

// Cycle keys look like "2026-07-07_12" (date_UTC-hour), matching the
// format used by multi-day.html's multi_active_cycles.json.
function formatCycleLabel(cycle) {
  const [date, hh] = String(cycle).split('_');
  return `${date} ${hh}:00 UTC`;
}

function getCurrentForecastCycle() {
  return forecastCycleSelect.value || null;
}

async function initStationCatalog() {
  try {
    const catalog = await loadStationCatalog();
    stationById = new Map(catalog.map(s => [String(s.station_id), s]));
  } catch (err) {
    console.warn('station catalog unavailable', err);
    stationById = new Map();
  }
}

async function refreshCycleStations() {
  const cycle = getCurrentForecastCycle();
  if (!cycle) {
    cycleStationIds = [];
    return;
  }
  try {
    cycleStationIds = await loadSuccessfulStationIds(cycle);
  } catch (err) {
    console.warn('successful_stations unavailable for', cycle, err);
    cycleStationIds = [];
  }
}

function getStationMarkerData() {
  return cycleStationIds
    .map(id => stationById.get(String(id)))
    .filter(Boolean)
    .map(s => ({
      stationId: String(s.station_id),
      name: s.station_name,
      lon: s.lon,
      lat: s.lat
    }));
}

function createTrackLayers() {
  const layers = [];
  if (trackResult?.paths?.length) {
    layers.push(
      new PathLayer({
        id: 'track-paths',
        data: trackResult.paths,
        getPath: d => d.path,
        getColor: d => d.color,
        widthUnits: 'pixels',
        getWidth: 3,
        widthMinPixels: 2,
        jointRounded: true,
        capRounded: true,
        pickable: false,
        parameters: {depthCompare: 'always', depthWriteEnabled: false}
      })
    );
  }
  if (trackMarkers?.length) {
    layers.push(
      new ScatterplotLayer({
        id: 'track-markers',
        data: trackMarkers,
        getPosition: d => [d.lon, d.lat, 0],
        getFillColor: d => {
          const c = [236, 72, 153, 230];
          const palette = [
            [236, 72, 153, 230],
            [59, 130, 246, 230],
            [16, 185, 129, 230],
            [245, 158, 11, 230],
            [139, 92, 246, 230],
            [239, 68, 68, 230],
            [14, 165, 233, 230],
            [132, 204, 22, 230],
            [249, 115, 22, 230],
            [168, 85, 247, 230]
          ];
          return palette[(Math.max(1, d.id) - 1) % palette.length] || c;
        },
        getRadius: 6,
        radiusUnits: 'pixels',
        stroked: true,
        getLineColor: [255, 255, 255, 220],
        lineWidthUnits: 'pixels',
        getLineWidth: 1.5,
        pickable: false,
        parameters: {depthCompare: 'always', depthWriteEnabled: false}
      })
    );
  }
  if (anomalyMarkers?.length) {
    layers.push(
      new ScatterplotLayer({
        id: 'anomaly-markers',
        data: anomalyMarkers,
        getPosition: d => [d.lon, d.lat, 0],
        getFillColor: d => {
          if (d.focused) return [255, 230, 120, 255];
          const palette = [
            [212, 160, 23, 240],
            [180, 130, 20, 240],
            [245, 190, 60, 240],
            [160, 120, 30, 240],
            [230, 170, 40, 240],
            [140, 100, 20, 240],
            [250, 210, 80, 240],
            [190, 145, 35, 240],
            [170, 125, 25, 240],
            [220, 175, 50, 240],
            [200, 150, 30, 240],
            [155, 115, 22, 240],
            [235, 185, 55, 240],
            [175, 135, 28, 240],
            [210, 165, 45, 240]
          ];
          return palette[(Math.max(1, d.id) - 1) % palette.length];
        },
        getRadius: d => (d.focused ? 9 : 7),
        radiusUnits: 'pixels',
        stroked: true,
        getLineColor: d => (d.focused ? [120, 80, 0, 255] : [255, 255, 255, 220]),
        lineWidthUnits: 'pixels',
        getLineWidth: d => (d.focused ? 2.5 : 1.5),
        pickable: false,
        parameters: {depthCompare: 'always', depthWriteEnabled: false}
      })
    );
  }
  return layers;
}

function setTrackMarkers(markers) {
  trackMarkers = Array.isArray(markers) ? markers : [];
  if (deckInstance) renderVisible();
}

function setTrackResult(result) {
  trackResult = result || null;
  if (deckInstance) renderVisible();
}

function setTrackPlaceMode(on) {
  trackPlaceMode = Boolean(on);
}

function setAnomalyMarkers(markers) {
  anomalyMarkers = Array.isArray(markers) ? markers : [];
  if (deckInstance) renderVisible();
}

function setAnomalySelectMode(on) {
  anomalySelectMode = Boolean(on);
}

function createStationLayer() {
  if (!showStations) return null;
  const data = getStationMarkerData();
  if (!data.length) return null;

  return new ScatterplotLayer({
    id: 'stations',
    data,
    getPosition: d => [d.lon, d.lat, 0],
    getRadius: 12000,
    radiusUnits: 'meters',
    radiusMinPixels: 5,
    radiusMaxPixels: 14,
    getFillColor: [124, 58, 237, 230],
    getLineColor: [255, 255, 255, 220],
    lineWidthUnits: 'pixels',
    getLineWidth: 1.5,
    stroked: true,
    pickable: true,
    autoHighlight: true,
    highlightColor: [255, 220, 80, 255],
    getTooltip: ({object}) =>
      object && {
        html: `<div style="font:12px sans-serif;"><strong>${object.name}</strong><br>${object.stationId}</div>`,
        style: {background: 'rgba(255,255,255,0.95)', color: '#111', padding: '6px 8px', borderRadius: '4px'}
      },
    parameters: {
      // Mesh fill sits on the same globe surface and writes depth; skip
      // the depth test so markers always composite above the contour.
      depthCompare: 'always',
      depthWriteEnabled: false
    }
  });
}

function sortCyclesDesc(cycles) {
  return [...cycles].sort((a, b) => (a < b ? 1 : a > b ? -1 : 0));
}

// Manifest timestamps look like "2026-07-07 01:00:00". Parsed as plain
// strings (not `new Date(...)`) since these are naive local timestamps
// with no timezone attached -- going through Date could silently shift
// them by the browser's local offset.
function formatTickLabel(ts) {
  const parts = String(ts).split(' ');
  if (parts.length !== 2) return String(ts);
  const [datePart, timePart] = parts;
  const [, month, day] = datePart.split('-');
  return `${month}/${day} ${timePart.slice(0, 5)}`;
}

// A run can have hundreds of timesteps -- labeling every single tick
// would make them illegible overlapping text. Thin them out to a
// reasonable max count, always keeping the first and last.
const MAX_TIMESTEP_LABELS = 15;

function buildTimestepTicks() {
  const n = manifest.timesteps.length;

  timestepTicksList.innerHTML = '';
  manifest.timesteps.forEach((_, i) => {
    const opt = document.createElement('option');
    opt.value = String(i);
    timestepTicksList.appendChild(opt);
  });

  const stride = Math.max(1, Math.ceil(n / MAX_TIMESTEP_LABELS));

  timestepLabelsEl.innerHTML = '';
  manifest.timesteps.forEach((ts, i) => {
    if (i % stride !== 0 && i !== n - 1) return;
    const pct = n > 1 ? (i / (n - 1)) * 100 : 0;
    const span = document.createElement('span');
    span.style.left = `${pct}%`;
    // Default CSS centers each label under its tick (translateX(-50%)),
    // which pushes the first label's left half and the last label's
    // right half outside the panel's edges. Anchor those two flush to
    // the inside instead so nothing overhangs the box.
    if (i === 0) {
      span.style.transform = 'translateX(0)';
    } else if (i === n - 1) {
      span.style.transform = 'translateX(-100%)';
    }
    span.textContent = formatTickLabel(ts);
    span.dataset.index = String(i);
    timestepLabelsEl.appendChild(span);
  });

  updateActiveTickLabel();
}

function updateActiveTickLabel() {
  for (const span of timestepLabelsEl.children) {
    const active = Number(span.dataset.index) === timestep;
    span.style.fontWeight = active ? '700' : '400';
    span.style.color = active ? '#111' : '#777';
  }
}

function setTimestep(newTimestep) {
  timestep = newTimestep;
  timestepBigInput.value = String(timestep);
  ofclTileWindCache.clear();
  if (stormHoverTipEl) {
    stormHoverTipEl.classList.add('hidden');
    delete stormHoverTipEl.dataset.stormKey;
  }
  updateActiveTickLabel();
  refresh();
}

function basemapTileBounds(tile) {
  const {boundingBox, index} = tile;
  let west = boundingBox[0][0];
  let south = boundingBox[0][1];
  let east = boundingBox[1][0];
  let north = boundingBox[1][1];
  // Belt-and-suspenders: stretch the top/bottom Mercator row to the pole so
  // the ~5° gap above ±85° isn't a black hole (matches deck.gl #10208).
  if (index) {
    const scale = 2 ** index.z;
    if (index.y === 0) north = 90;
    if (index.y === scale - 1) south = -90;
  }
  return [west, south, east, north];
}

// Stable callback + layer instance: renderVisible() runs on every mesh
// update and used to pass a fresh arrow function as renderSubLayers, which
// deck.gl treats as a prop change and nulls every cached tile sublayer --
// the basemap would vanish and reload tile-by-tile (very noticeable while
// rotating toward Antarctica or whenever tiles finish loading).
function renderBasemapSubLayers(props) {
  return new BitmapLayer(props, {
    data: null,
    image: props.data,
    bounds: basemapTileBounds(props.tile),
    // GlobeView already sets cullMode:'back' — don't override with 'none'
    // (that drew the far hemisphere through the near one when zoomed out).
    // Depth write lets near-side tiles occlude far-side tiles and stops
    // parent/child overlap z-fighting from looking like transparent holes.
    parameters: {
      depthWriteEnabled: true,
      depthCompare: 'less-equal'
    }
  });
}

function createBasemapLayer() {
  const style = BASEMAP_STYLES[basemapStyle] || BASEMAP_STYLES.osm;
  return new TileLayer({
    id: 'basemap',
    data: style.data,
    minZoom: 0,
    maxZoom: style.maxZoom,
    tileSize: 256,
    refinementStrategy: 'no-overlap',
    renderSubLayers: renderBasemapSubLayers
  });
}

let basemapLayerInstance = createBasemapLayer();

// Globe culling uses computeGlobeCullMinDot() per viewport -- see quadtree.js.

/** Merged mesh buffers keyed by timestep|field|tile-set (overs vs leaves). */
let meshCompositeCache = new Map();
let lastGlobeCullMinDot = 0;
let tileLoadScheduler = null;

function getFieldData(entry, fieldConfig) {
  if (fieldConfig.derived === 'depth') {
    return entry.depth || null;
  }
  if (fieldConfig.derived === 'elementSize') {
    return entry.elementSize || null;
  }
  if (fieldConfig.derived === 'elementShape') {
    return entry.elementShape || null;
  }
  if (fieldConfig.vectorField) {
    const byTimestep = entry[`${fieldConfig.vectorField}SpeedByTimestep`];
    return byTimestep && byTimestep.get(timestep);
  }
  return entry.scalarByTimestep.get(timestep);
}

function collectReadyTiles(visible) {
  const fieldConfig = DISPLAY_FIELDS[displayField];
  const tiles = [];
  let vertCount = 0;

  for (const [z, x, y] of visible) {
    const entry = store.get(z, x, y);
    if (!entry || entry.status !== 'geometry-ready') continue;
    const fieldData = getFieldData(entry, fieldConfig);
    if (!fieldData) continue;

    tiles.push({z, x, y, entry, fieldData, nVerts: entry.nVerts});
    vertCount += entry.nVerts;
  }

  return {tiles, vertCount};
}

function getMergedMesh(selected) {
  const key = [
    timestep,
    displayField,
    selected.map(t => `${t.z}/${t.x}/${t.y}`).join('|')
  ].join(';');

  const cached = meshCompositeCache.get(key);
  if (cached) return cached;

  const merged = mergeMeshTiles(
    selected.map(c => ({
      positions3: c.entry.positions3,
      indices: c.entry.indices,
      scalar: c.fieldData.scalar,
      wetMask: c.fieldData.wetMask,
      nVerts: c.entry.nVerts
    }))
  );

  // Bound memory: keep the two most recent composites (overs + leaves).
  if (meshCompositeCache.size >= 4) meshCompositeCache.clear();
  meshCompositeCache.set(key, merged);
  return merged;
}

function pushMeshComposite(meshLayers, id, selected, edges) {
  if (!selected.length) return;
  const merged = getMergedMesh(selected);
  meshLayers.push(
    new MeshTileLayer({
      id,
      positions3: merged.positions3,
      triangleIndices: merged.triangleIndices,
      scalarValues: merged.scalarValues,
      wetMaskValues: merged.wetMaskValues,
      colorDomain: [domainMin, domainMax],
      colormapId: getColormap(colormapId).glslId,
      opacity: dataOpacity,
      showEdges: edges
    })
  );
}

// Recomputes the visible tile set (overview LOD and/or leaves) from the
// current viewport and kicks off loads for anything missing. Called on
// viewport change and timestep change -- NOT on every individual
// tile-load completion (that would mean re-walking the quadtree and
// rebuilding the full layer array once per tile as a burst of loads
// resolves, which stalls the main thread when a few hundred tiles land
// at once).
//
// GlobeView needs a fundamentally different culling test than a flat map:
// a lon/lat bounding box is the wrong shape for what's visible on a
// rotating sphere (degenerates near poles/antimeridian -- see the big
// comment above collectVisibleLeavesGlobe() in quadtree.js), so it gets
// its own 3D front-facing test instead of viewport.getBounds().
function currentLodOptions(viewportZoom) {
  const fieldConfig = DISPLAY_FIELDS[displayField];
  const needVectors = Boolean(activeVectorField || fieldConfig?.vectorField);
  return {
    viewportZoom,
    timestep,
    needVectors,
    forceLeaves: false
  };
}

function refresh() {
  if (!deckInstance || !store || !index) return;

  const viewport = deckInstance.getViewports()[0];
  if (!viewport) return;
  if (!Number.isFinite(viewport.longitude) || !Number.isFinite(viewport.latitude)) return;

  const lod = currentLodOptions(viewport.zoom);

  let visible;
  if (viewport.constructor.name === 'GlobeViewport') {
    lastGlobeCullMinDot = computeGlobeCullMinDot(viewport);
    const screenBBox = computeGlobeScreenBBox(viewport);
    visible = store.getVisibleLeavesGlobe(
      viewport.longitude,
      viewport.latitude,
      lastGlobeCullMinDot,
      screenBBox,
      lod
    );
  } else {
    visible = store.getVisibleLeaves(viewport.getBounds(), lod);
  }
  lastVisible = visible;

  tileLoadScheduler.syncVisible(visible, viewport.longitude, viewport.latitude, {
    zoom: viewport.zoom,
    hasOverviews: index.hasOverviews,
    overviewMaxZoom: index.overviewMaxZoom
  });

  renderVisible();
}

function isOverviewTile(z, x, y) {
  return Boolean(index?.overviewSet?.has(`${z},${x},${y}`));
}

function tileNeedsLoad(z, x, y) {
  const entry = store.get(z, x, y);
  if (!entry || entry.status !== 'geometry-ready') return true;

  const fieldConfig = DISPLAY_FIELDS[displayField];
  const overview = isOverviewTile(z, x, y);
  if (fieldConfig.derived === 'depth') {
    if (!manifest?.has_depth) return false;
    if (!entry.depth) return true;
  } else if (fieldConfig.derived === 'elementSize') {
    if (!entry.elementSize) return true;
  } else if (fieldConfig.derived === 'elementShape') {
    if (!entry.elementShape) return true;
  } else {
    if (!entry.scalarByTimestep.has(timestep)) return true;
    // Overviews often lack velocity/wind — don't keep retrying forever.
    if (fieldConfig.vectorField && !(overview && !index.overviewHasVectors)) {
      const byTimestep = entry[`${fieldConfig.vectorField}SpeedByTimestep`];
      if (!byTimestep?.has(timestep)) return true;
    }
  }

  if (activeVectorField && !(overview && !index.overviewHasVectors)) {
    const byTimestep = entry[`${activeVectorField}ByTimestep`];
    if (!byTimestep?.has(timestep)) return true;
  }

  return false;
}

async function loadTile(z, x, y, generation) {
  if (!tileLoadScheduler.isVisible(z, x, y) || generation !== tileLoadScheduler.generation) {
    return;
  }
  const t0 = diagNow();
  // null = aborted (viewport moved); only count completed attempts
  let outcome = null;
  try {
    await store.ensureGeometry(z, x, y);
    if (generation !== tileLoadScheduler.generation) return;

    const fieldConfig = DISPLAY_FIELDS[displayField];
    const overview = isOverviewTile(z, x, y);
    if (fieldConfig.derived === 'depth') {
      if (!manifest?.has_depth) return;
      await store.ensureDepth(z, x, y);
      if (generation !== tileLoadScheduler.generation) return;
    } else if (fieldConfig.derived === 'elementSize') {
      await store.ensureElementSize(z, x, y);
      if (generation !== tileLoadScheduler.generation) return;
    } else if (fieldConfig.derived === 'elementShape') {
      await store.ensureElementShape(z, x, y);
      if (generation !== tileLoadScheduler.generation) return;
    } else {
      await store.ensureScalar(z, x, y, timestep);
      if (generation !== tileLoadScheduler.generation) return;

      if (fieldConfig.vectorField && !(overview && !index.overviewHasVectors)) {
        try {
          await store.ensureSpeed(z, x, y, timestep, fieldConfig.vectorField);
        } catch (err) {
          if (!overview) throw err;
          console.warn('overview vector/speed missing', z, x, y, err);
        }
        if (generation !== tileLoadScheduler.generation) return;
      }
    }

    if (activeVectorField && !(overview && !index.overviewHasVectors)) {
      try {
        await store.ensureVector(z, x, y, timestep, activeVectorField);
        if (generation !== tileLoadScheduler.generation) return;
        particleAnim.invalidateIndex();
      } catch (err) {
        if (!overview) throw err;
        console.warn('overview particle vector missing', z, x, y, err);
      }
    }
    outcome = 'ok';
  } catch (err) {
    console.error('tile load failed', z, x, y, err);
    outcome = 'fail';
  } finally {
    if (outcome) {
      const snap = tileLoadScheduler.snapshot();
      tileDiag.tileDone({
        z,
        x,
        y,
        ok: outcome === 'ok',
        totalMs: diagNow() - t0,
        queueLeft: snap.queueLeft,
        inFlight: snap.inFlight
      });
    }
  }
  if (outcome === 'ok') scheduleRenderVisible();
}

// Rebuilds the layer array from whatever is currently cached for
// `lastVisible` and pushes it to deck.gl. Cheap to call repeatedly since
// tile buffers are reference-stable, so unchanged MeshTileLayers are a
// no-op for deck.gl's diffing / no GPU re-upload.
function renderVisible() {
  if (!deckInstance) return;
  const viewport = deckInstance.getViewports()[0];
  if (!viewport) return;

  const {tiles, vertCount} = collectReadyTiles(lastVisible);

  const meshLayers = [];
  let mergeMs = 0;
  if (tiles.length > 0) {
    const mergeT0 = diagNow();
    // Wireframe is leaf-only: overview remesh edges are dense/misleading.
    const overs = [];
    const leaves = [];
    for (const t of tiles) {
      if (isOverviewTile(t.z, t.x, t.y)) overs.push(t);
      else leaves.push(t);
    }
    if (overs.length && leaves.length) {
      pushMeshComposite(meshLayers, 'mesh-composite-overviews', overs, false);
      pushMeshComposite(meshLayers, 'mesh-composite-leaves', leaves, showEdges);
    } else if (overs.length) {
      pushMeshComposite(meshLayers, 'mesh-composite', overs, false);
    } else {
      pushMeshComposite(meshLayers, 'mesh-composite', leaves, showEdges);
    }
    mergeMs = diagNow() - mergeT0;
  }

  const showBasemap = !window.__debugNoBasemap;
  const stationLayer = createStationLayer();
  let ofclLayers = [];
  if (showOfcl) {
    try {
      ofclLayers = createOfclLayers(
        {PathLayer, ScatterplotLayer, TextLayer},
        ofclStorms,
        getOfclPositions()
      );
    } catch (err) {
      console.warn('[ofcl] layer build failed', err);
      ofclLayers = [];
    }
  }
  const layers = [...(showBasemap ? [basemapLayerInstance] : []), ...meshLayers];
  if (stationLayer) layers.push(stationLayer);
  layers.push(...ofclLayers);
  layers.push(...createTrackLayers());
  const propsT0 = diagNow();
  deckInstance.setProps({layers});
  const setPropsMs = diagNow() - propsT0;

  tileDiag.render({
    ready: tiles.length,
    visible: lastVisible.length,
    mergeMs,
    setPropsMs,
    vertCount,
    forceLog:
      tiles.length > 0 &&
      (tiles.length === lastVisible.length || tiles.length % 50 === 0)
  });

  const zoomText = viewport.zoom.toFixed(2);
  const overviewNote = index.hasOverviews
    ? `  |  overs ${manifest.overviews?.length || 0}` +
      (index.overviewMaxZoom != null ? ` (≤z${index.overviewMaxZoom})` : '') +
      ` @zoom≤${index.overviewMaxViewportZoom ?? 5}`
    : '';
  setStatus(
    `leaf tiles total: ${manifest.tiles.length}${overviewNote}\n` +
    `visible: ${lastVisible.length}  |  ready: ${tiles.length}  |  zoom: ${zoomText}\n` +
    `timestep ${timestep}: ${manifest.timesteps[timestep] || ''}`
  );
}

// Throttle, not debounce: each tile-load completion calls this, and with
// dozens/hundreds landing in a steady trickle a debounce (reset the timer
// on every call) would never go quiet long enough to fire until the very
// last tile arrived -- the map would jump straight from 0 ready to N
// ready instead of filling in progressively. A throttle instead renders
// at a fixed ~120ms cadence: the first call in a window schedules the
// render, later calls within that window are no-ops, and the timer
// resets only after it actually fires -- so it also guarantees one final
// render right after the last tile completes.
let renderTimer = null;
let renderAgain = false;
function scheduleRenderVisible() {
  if (renderTimer) {
    // Scalar/geometry for other tiles may finish while a throttled render is
    // already queued. Remember to run once more so late tiles aren't skipped.
    renderAgain = true;
    return;
  }
  renderTimer = setTimeout(() => {
    renderTimer = null;
    renderVisible();
    if (renderAgain) {
      renderAgain = false;
      scheduleRenderVisible();
    }
  }, lastVisible.length > 100 ? 200 : lastVisible.length > 50 ? 160 : 120);
}

// Finds the closest mesh vertex (lon/lat, straight-line distance -- fine
// at click precision) to a clicked point, searching only tiles that are
// Click picking: require the click to fall inside a loaded mesh triangle.
// (The old nearest-vertex scan snapped across land / empty ocean to whatever
// node was closest, which produced plots for clicks with no mesh under them.)
function unwrapLonNear(lon, refLon) {
  let x = lon;
  while (x - refLon > 180) x -= 360;
  while (x - refLon < -180) x += 360;
  return x;
}

function pointInTriangle(px, py, ax, ay, bx, by, cx, cy) {
  const v0x = cx - ax;
  const v0y = cy - ay;
  const v1x = bx - ax;
  const v1y = by - ay;
  const v2x = px - ax;
  const v2y = py - ay;
  const dot00 = v0x * v0x + v0y * v0y;
  const dot01 = v0x * v1x + v0y * v1y;
  const dot02 = v0x * v2x + v0y * v2y;
  const dot11 = v1x * v1x + v1y * v1y;
  const dot12 = v1x * v2x + v1y * v2y;
  const denom = dot00 * dot11 - dot01 * dot01;
  if (Math.abs(denom) < 1e-20) return false;
  const inv = 1 / denom;
  const u = (dot11 * dot02 - dot01 * dot12) * inv;
  const v = (dot00 * dot12 - dot01 * dot02) * inv;
  return u >= -1e-6 && v >= -1e-6 && u + v <= 1 + 1e-6;
}

function vertexDistSq(lon, lat, pos, index) {
  let dx = pos[index * 3] - lon;
  if (dx > 180) dx -= 360;
  if (dx < -180) dx += 360;
  const dy = pos[index * 3 + 1] - lat;
  return dx * dx + dy * dy;
}

/**
 * Return the mesh vertex to sample for a map click, or null if the click
 * is not inside any currently loaded / visible triangle.
 * Prefers leaf tiles over overview tiles when both contain the point.
 */
function findMeshClickHit(lon, lat) {
  let bestLeaf = null;
  let bestOverview = null;

  for (const [z, x, y] of lastVisible) {
    const entry = store.get(z, x, y);
    if (!entry || entry.status !== 'geometry-ready') continue;
    const pos = entry.positions3;
    const idx = entry.indices;
    if (!pos || !idx || idx.length < 3) continue;

    const isOverview = isOverviewTile(z, x, y);
    // Cheap reject: tile lon/lat AABB (with a small pad).
    let minLon = Infinity;
    let maxLon = -Infinity;
    let minLat = Infinity;
    let maxLat = -Infinity;
    const nVerts = pos.length / 3;
    for (let i = 0; i < nVerts; i++) {
      const vx = unwrapLonNear(pos[i * 3], lon);
      const vy = pos[i * 3 + 1];
      if (vx < minLon) minLon = vx;
      if (vx > maxLon) maxLon = vx;
      if (vy < minLat) minLat = vy;
      if (vy > maxLat) maxLat = vy;
    }
    if (lon < minLon - 2 || lon > maxLon + 2 || lat < minLat - 2 || lat > maxLat + 2) {
      continue;
    }

    const nTris = idx.length / 3;
    for (let t = 0; t < nTris; t++) {
      const i0 = idx[t * 3];
      const i1 = idx[t * 3 + 1];
      const i2 = idx[t * 3 + 2];
      const ax = unwrapLonNear(pos[i0 * 3], lon);
      const ay = pos[i0 * 3 + 1];
      const bx = unwrapLonNear(pos[i1 * 3], lon);
      const by = pos[i1 * 3 + 1];
      const cx = unwrapLonNear(pos[i2 * 3], lon);
      const cy = pos[i2 * 3 + 1];
      if (!pointInTriangle(lon, lat, ax, ay, bx, by, cx, cy)) continue;

      // Nearest corner of the containing triangle.
      let vertexIndex = i0;
      let distSq = vertexDistSq(lon, lat, pos, i0);
      const d1 = vertexDistSq(lon, lat, pos, i1);
      if (d1 < distSq) {
        distSq = d1;
        vertexIndex = i1;
      }
      const d2 = vertexDistSq(lon, lat, pos, i2);
      if (d2 < distSq) {
        distSq = d2;
        vertexIndex = i2;
      }

      const hit = {
        z,
        x,
        y,
        vertexIndex,
        distSq,
        lon: pos[vertexIndex * 3],
        lat: pos[vertexIndex * 3 + 1]
      };
      if (isOverview) {
        if (!bestOverview || distSq < bestOverview.distSq) bestOverview = hit;
      } else if (!bestLeaf || distSq < bestLeaf.distSq) {
        bestLeaf = hit;
      }
      // One hit per tile is enough; keep scanning other tiles.
      break;
    }
  }

  return bestLeaf || bestOverview;
}

// Nearest loaded vertex (no containment). Used for storm-marker tile wind
// sampling where the storm center is already known to be over water.
function findNearestVertex(lon, lat) {
  let best = null;
  for (const [z, x, y] of lastVisible) {
    const entry = store.get(z, x, y);
    if (!entry || entry.status !== 'geometry-ready') continue;
    const pos = entry.positions3;
    const n = pos.length / 3;
    for (let i = 0; i < n; i++) {
      const distSq = vertexDistSq(lon, lat, pos, i);
      if (!best || distSq < best.distSq) {
        best = {z, x, y, vertexIndex: i, distSq, lon: pos[i * 3], lat: pos[i * 3 + 1]};
      }
    }
  }
  return best;
}

// Plots whatever the fill layer currently shows (Water Level, Water
// Speed, or Wind Speed) -- same DISPLAY_FIELDS config that drives
// renderVisible()/loadTile(), so the two are always in sync.
const HYDROGRAPH_CSV_COLUMNS = [
  'datetime',
  'value',
  'lon',
  'lat',
  'station_id',
  'station_name',
  'series'
];

function csvEscape(value) {
  if (value === null || value === undefined) return '';
  const s = String(value);
  if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

function rowsToCsv(rows, columns = HYDROGRAPH_CSV_COLUMNS) {
  const lines = [columns.join(',')];
  for (const row of rows) {
    lines.push(columns.map(col => csvEscape(row[col])).join(','));
  }
  return `${lines.join('\n')}\n`;
}

function clearHydrographCsv() {
  hydrographCsvDownload = null;
  if (hydrographDownloadCsv) {
    hydrographDownloadCsv.classList.add('hidden');
  }
}

function setHydrographCsv(rows, filename) {
  if (!rows?.length) {
    clearHydrographCsv();
    return;
  }
  hydrographCsvDownload = {rows, filename};
  if (hydrographDownloadCsv) {
    hydrographDownloadCsv.classList.remove('hidden');
  }
}

function downloadHydrographCsv() {
  if (!hydrographCsvDownload?.rows?.length) return;
  const csv = rowsToCsv(hydrographCsvDownload.rows);
  const blob = new Blob([csv], {type: 'text/csv;charset=utf-8'});
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = hydrographCsvDownload.filename || 'hydrograph.csv';
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

// Station hydrograph from json_stations (multi-day.html style).
async function showStationHydrograph(stationId) {
  const cycle = getCurrentForecastCycle();
  if (!cycle) return;

  const meta = stationById.get(String(stationId));
  if (!meta) return;

  selectedStationId = String(stationId);
  const reqId = ++hydrographRequestId;
  clearHydrographCsv();

  hydrographModal.classList.remove('hidden');
  hydrographTitle.textContent = `${meta.station_name} (${stationId})`;
  hydrographSubtitle.textContent = `Forecast cycle: ${formatCycleLabel(cycle)}`;
  hydrographLegend.style.display = 'flex';
  hydrographStatus.textContent = 'Loading...';
  hydrographStatus.style.display = 'block';
  hydrographPlot.style.display = 'none';

  const records = await loadStationSeries(cycle, stationId);
  if (reqId !== hydrographRequestId) return;

  // Plotly needs a visible container to autosize correctly.
  hydrographStatus.style.display = 'none';
  hydrographPlot.style.display = 'block';

  const result = renderStationHydrograph(hydrographPlot, records, cycle, {
    cycleLabel: formatCycleLabel(cycle)
  });

  if (result.empty) {
    hydrographStatus.textContent = result.message;
    hydrographStatus.style.display = 'block';
    hydrographPlot.style.display = 'none';
    return;
  }

  const rows = stationRecordsToCsvRows(records, {
    lon: meta.lon,
    lat: meta.lat,
    stationId: String(stationId),
    stationName: meta.station_name
  });
  setHydrographCsv(rows, `station_${stationId}_${cycle}.csv`);
}

// Mesh-vertex hydrograph (click on the mesh only — not nearest-node snap).
async function showMeshHydrograph(lon, lat) {
  const nearest = findMeshClickHit(lon, lat);
  if (!nearest) return;
  const {z, x, y, vertexIndex} = nearest;
  const fieldConfig = DISPLAY_FIELDS[displayField];
  clearHydrographCsv();

  hydrographModal.classList.remove('hidden');
  hydrographTitle.textContent = `${fieldConfig.label} @ ${nearest.lat.toFixed(4)}, ${nearest.lon.toFixed(4)}`;
  hydrographLegend.style.display = 'none';
  selectedStationId = null;
  hydrographStatus.textContent = 'Loading...';
  hydrographStatus.style.display = 'block';
  hydrographPlot.style.display = 'none';

  // Depth is static bathymetry — no time series to fetch.
  if (fieldConfig.derived === 'depth') {
    hydrographSubtitle.textContent = 'Mesh vertex depth below geoid (static)';
    await store.ensureDepth(z, x, y);
    const fd = store.get(z, x, y)?.depth;
    const v = fd?.scalar[vertexIndex];
    if (v === undefined || v === FILL_VALUE) {
      hydrographStatus.textContent = 'No depth at this vertex.';
      return;
    }
    hydrographStatus.textContent = `${fieldConfig.label}: ${v.toFixed(3)} m`;
    hydrographStatus.style.display = 'block';
    hydrographPlot.style.display = 'none';
    setHydrographCsv(
      [
        {
          datetime: '',
          value: v,
          lon: nearest.lon,
          lat: nearest.lat,
          station_id: '',
          station_name: '',
          series: fieldConfig.label
        }
      ],
      `mesh_depth_${nearest.lat.toFixed(4)}_${nearest.lon.toFixed(4)}.csv`
    );
    return;
  }

  // Element size / shape are static geometry — no time series to fetch.
  if (fieldConfig.derived === 'elementSize' || fieldConfig.derived === 'elementShape') {
    const isShape = fieldConfig.derived === 'elementShape';
    hydrographSubtitle.textContent = isShape
      ? 'Mesh vertex element shape (equilateral quality 0–1, static)'
      : 'Mesh vertex element size (circumdiameter, static)';
    if (isShape) await store.ensureElementShape(z, x, y);
    else await store.ensureElementSize(z, x, y);
    const fd = isShape
      ? store.get(z, x, y)?.elementShape
      : store.get(z, x, y)?.elementSize;
    const v = fd?.scalar[vertexIndex];
    if (v === undefined || v === FILL_VALUE) {
      hydrographStatus.textContent = isShape
        ? 'No element shape at this vertex.'
        : 'No element size at this vertex.';
      return;
    }
    hydrographStatus.textContent = isShape
      ? `${fieldConfig.label}: ${v.toFixed(3)}`
      : `${fieldConfig.label}: ${v.toFixed(3)} km`;
    hydrographStatus.style.display = 'block';
    hydrographPlot.style.display = 'none';
    setHydrographCsv(
      [
        {
          datetime: '',
          value: v,
          lon: nearest.lon,
          lat: nearest.lat,
          station_id: '',
          station_name: '',
          series: fieldConfig.label
        }
      ],
      `mesh_${isShape ? 'elementshape' : 'elementsize'}_${nearest.lat.toFixed(4)}_${nearest.lon.toFixed(4)}.csv`
    );
    return;
  }

  hydrographSubtitle.textContent = 'Mesh vertex time series (current forecast cycle)';

  const fetchTimestep = fieldConfig.vectorField
    ? t => store.ensureSpeed(z, x, y, t, fieldConfig.vectorField)
    : t => store.ensureScalar(z, x, y, t);

  const fieldDatas = await Promise.all(manifest.timesteps.map((_, t) => fetchTimestep(t)));
  const values = fieldDatas.map(fd => {
    const v = fd.scalar[vertexIndex];
    return v === FILL_VALUE ? null : v;
  });
  const xTimes = manifest.timesteps.map(ts => String(ts).replace(' ', 'T'));

  if (values.every(v => v === null)) {
    hydrographStatus.textContent = 'This point is dry (on land) for the entire simulation.';
    return;
  }

  hydrographStatus.style.display = 'none';
  hydrographPlot.style.display = 'block';

  Plotly.newPlot(
    hydrographPlot,
    [
      {
        x: xTimes,
        y: values,
        type: 'scatter',
        mode: 'lines',
        line: {color: '#7c3aed'},
        connectgaps: false,
        name: fieldConfig.label
      }
    ],
    {
      margin: {t: 10, r: 20, b: 50, l: 55},
      height: 320,
      autosize: true,
      xaxis: {title: 'Date/Time'},
      yaxis: {title: fieldConfig.label}
    },
    {responsive: true, displayModeBar: false}
  ).then(() => {
    requestAnimationFrame(() => Plotly.Plots.resize(hydrographPlot));
  });

  const rows = [];
  for (let i = 0; i < xTimes.length; i++) {
    if (values[i] === null || values[i] === undefined) continue;
    rows.push({
      datetime: xTimes[i],
      value: values[i],
      lon: nearest.lon,
      lat: nearest.lat,
      station_id: '',
      station_name: '',
      series: fieldConfig.label
    });
  }
  const cycle = getCurrentForecastCycle() || 'cycle';
  setHydrographCsv(
    rows,
    `mesh_${nearest.lat.toFixed(4)}_${nearest.lon.toFixed(4)}_${cycle}.csv`
  );
}

function handleMapClick(info) {
  if (trackPlaceMode) return;
  if (anomalySelectMode) return; // placement handled by anomaly pointer listeners

  let coordinate = info && info.coordinate;
  if (!coordinate) {
    const viewport = deckInstance.getViewports()[0];
    if (!viewport) return;
    coordinate = viewport.unproject([info.x, info.y]);
  }
  if (!coordinate) return;
  const [lon, lat] = coordinate;

  // Prefer focusing a highlighted Anomaly Node over opening a hydrograph.
  if (anomalyApi?.handleMapClick?.(lon, lat)) {
    return;
  }

  if (info?.object?.stationId) {
    showStationHydrograph(info.object.stationId).catch(err => {
      console.error('station hydrograph failed', err);
      hydrographStatus.textContent = `Error: ${err.message}`;
      hydrographStatus.style.display = 'block';
      hydrographPlot.style.display = 'none';
    });
    return;
  }

  showMeshHydrograph(lon, lat).catch(err => {
    console.error('hydrograph failed', err);
    hydrographStatus.textContent = `Error: ${err.message}`;
    hydrographStatus.style.display = 'block';
    hydrographPlot.style.display = 'none';
  });
}

let refreshTimer = null;
function scheduleRefresh() {
  clearTimeout(refreshTimer);
  refreshTimer = setTimeout(refresh, 150);
}

let currentViewState = null;

function onDeckViewStateChange({viewState}) {
  currentViewState = {
    ...viewState,
    latitude: clampViewLatitude(viewState.latitude)
  };
  if (deckInstance) {
    deckInstance.setProps({viewState: currentViewState});
  }
  scheduleRefresh();
}

function sleepMs(ms) {
  return new Promise(resolve => setTimeout(resolve, ms));
}

async function waitVisibleReady(maxMs = 45000) {
  const t0 = Date.now();
  while (Date.now() - t0 < maxMs) {
    refresh();
    if (lastVisible.length > 0) {
      const pending = lastVisible.some(([z, x, y]) => tileNeedsLoad(z, x, y));
      if (!pending) {
        await new Promise(resolve => {
          requestAnimationFrame(() => requestAnimationFrame(resolve));
        });
        return true;
      }
    }
    await sleepMs(120);
  }
  return false;
}

async function applyAnimSettings(job) {
  displayField = job.field;
  basemapStyle = job.basemapStyle;
  colormapId = job.colormapId;
  dataOpacity = Number.isFinite(job.dataOpacity) ? job.dataOpacity : 1;
  domainMin = job.domainMin;
  domainMax = job.domainMax;
  showEdges = Boolean(job.showEdges);
  showOfcl = Boolean(job.showOfcl);

  if (displayFieldSelect) displayFieldSelect.value = displayField;
  if (basemapSelect) basemapSelect.value = basemapStyle;
  if (colormapSelect) colormapSelect.value = colormapId;
  if (opacitySlider) {
    const pct = Math.round((1 - dataOpacity) * 100);
    opacitySlider.value = String(pct);
    opacityValueEl.textContent = `${pct}%`;
  }
  if (legendMinInput) legendMinInput.value = String(domainMin);
  if (legendMaxInput) legendMaxInput.value = String(domainMax);
  if (showEdgesToggle) showEdgesToggle.checked = showEdges;
  if (showOfclToggle) showOfclToggle.checked = showOfcl;

  legendBarEl.style.opacity = String(dataOpacity);
  basemapLayerInstance = createBasemapLayer();
  updateLegend();

  ofclTileWindCache.clear();
  if (showOfcl && !ofclStorms.length) {
    try {
      await refreshOfclStorms();
    } catch (err) {
      console.warn('[anim] ofcl refresh failed', err);
    }
  } else if (!showOfcl) {
    ofclStorms = [];
  }
  refresh();
}

function snapshotViewerState() {
  const vp = deckInstance?.getViewports()?.[0];
  return {
    displayField,
    basemapStyle,
    colormapId,
    dataOpacity,
    domainMin,
    domainMax,
    showEdges,
    showOfcl,
    timestep,
    viewState: currentViewState
      ? {...currentViewState}
      : vp
        ? {
            longitude: vp.longitude,
            latitude: vp.latitude,
            zoom: vp.zoom,
            pitch: vp.pitch || 0,
            bearing: vp.bearing || 0
          }
        : null
  };
}

async function restoreViewerState(snap) {
  if (!snap) return;
  await applyAnimSettings({
    field: snap.displayField,
    basemapStyle: snap.basemapStyle,
    colormapId: snap.colormapId,
    dataOpacity: snap.dataOpacity,
    domainMin: snap.domainMin,
    domainMax: snap.domainMax,
    showEdges: snap.showEdges,
    showOfcl: snap.showOfcl
  });
  if (snap.viewState) {
    currentViewState = {...snap.viewState};
    deckInstance.setProps({viewState: currentViewState});
  }
  setTimestep(snap.timestep);
  await waitVisibleReady(30000);
}

async function fitViewToBbox(bbox) {
  const vs = viewStateForBbox(bbox, clampViewLatitude);
  currentViewState = {...vs};
  deckInstance.setProps({viewState: currentViewState});
  await sleepMs(350);
  refresh();
}

function setUiChromeVisible(visible) {
  for (const id of ['topLeft', 'bottom-bar', 'legendStack', 'animModal', 'trackModal']) {
    const el = document.getElementById(id);
    if (!el) continue;
    el.style.visibility = visible ? '' : 'hidden';
  }
}

function setShowStations(visible) {
  showStations = Boolean(visible);
  if (deckInstance) renderVisible();
}

function getLegendDrawInfo() {
  const cmap = getColormap(colormapId);
  return {
    label: DISPLAY_FIELDS[displayField]?.label || displayField,
    domainMin,
    domainMax,
    opacity: dataOpacity,
    css: cmap.css,
    stops: cmap.stops || null
  };
}

let layoutRefreshObserver = null;
function attachLayoutRefreshObserver() {
  const mapEl = document.getElementById('map');
  if (!mapEl || layoutRefreshObserver || typeof ResizeObserver === 'undefined') return;
  layoutRefreshObserver = new ResizeObserver(() => scheduleRefresh());
  layoutRefreshObserver.observe(mapEl);
}

function scheduleInitialRefresh() {
  scheduleRefresh();
  requestAnimationFrame(() => scheduleRefresh());
}

// Show/hide display-field options that depend on per-cycle manifest flags
// (currently Depth via has_depth). If the active field becomes unavailable,
// fall back to Water Level so we don't keep requesting missing tiles.
function syncDisplayFieldOptions() {
  if (!displayFieldSelect) return;
  const hasDepth = Boolean(manifest?.has_depth);
  const depthOpt = displayFieldSelect.querySelector('option[value="depth"]');
  if (depthOpt) {
    depthOpt.hidden = !hasDepth;
    depthOpt.disabled = !hasDepth;
  }
  if (displayField === 'depth' && !hasDepth) {
    displayField = 'waterlevel';
    displayFieldSelect.value = 'waterlevel';
    colormapId = DEFAULT_COLORMAP_ID;
    if (colormapSelect) colormapSelect.value = DEFAULT_COLORMAP_ID;
    resetLegendDomainInputs();
    updateLegend();
  } else if (displayFieldSelect.value !== displayField) {
    displayFieldSelect.value = displayField;
  }
}

// Loads manifest.json from the current TILES_BASE_URL and (re)builds the
// quadtree index + tile store against it. Shared by the initial load and
// by forecast-cycle switching -- both need the exact same reset sequence
// (new store, new index, fresh timestep range, cleared viewport cache) or
// stale geometry/timesteps from the previous cycle would leak through.
async function loadManifestAndTiles() {
  setStatus('loading manifest ...');
  const res = await fetch(`${TILES_BASE_URL}/manifest.json`, {cache: 'no-cache'});
  if (!res.ok) throw new Error(`manifest fetch failed: ${TILES_BASE_URL}/manifest.json -> ${res.status}`);
  manifest = await res.json();
  // Bust geometry Cache Storage when overs are re-remeshed (same URL paths).
  setGeometryContentRevision(
    manifest.tiles_revision ||
      manifest.overview_content_revision ||
      `${manifest.overview_meshing || 'none'}-${manifest.overviews?.length || 0}`
  );
  index = buildQuadtreeIndex(manifest);
  store = new TileStore(TILES_BASE_URL, index);
  tileLoadScheduler = new TileLoadScheduler({
    loadOne: loadTile,
    needsLoad: tileNeedsLoad,
    getIndex: () => store.index
  });
  window.__debug = {
    manifest,
    index,
    store,
    getLastVisible: () => lastVisible,
    getDeck: () => deckInstance,
    seamDebug: isSeamDebugEnabled(),
    dumpSeamTile(z, x, y, timestepIndex = timestep) {
      const entry = store?.get(z, x, y);
      if (!entry) return console.warn('tile not loaded', z, x, y);
      const field = entry.scalarByTimestep.get(timestepIndex);
      if (!field) return console.warn('scalar not loaded for timestep', timestepIndex);
      return analyzeSeamTile(z, x, y, entry, field.scalar, field.wetMask);
    }
  };
  if (isSeamDebugEnabled()) {
    console.info('[seam-debug] enabled — add ?seamDebug=1 to URL. Use __debug.dumpSeamTile(z,x,y) for any loaded tile.');
  }

  // particleAnim is created after the very first load, so it doesn't
  // exist yet on that call -- only reset it on subsequent cycle switches.
  if (particleAnim) {
    particleAnim.store = store;
    particleAnim.invalidateIndex();
  }

  timestep = 0;
  lastVisible = [];
  meshCompositeCache.clear();
  timestepBigInput.value = '0';
  timestepBigInput.max = String(manifest.timesteps.length - 1);
  buildTimestepTicks();
  syncDisplayFieldOptions();
  resetLegendDomainInputs();
  updateLegend();

  await refreshCycleStations();
  if (selectedStationId && !hydrographModal.classList.contains('hidden')) {
    showStationHydrograph(selectedStationId).catch(err => {
      console.error('station hydrograph refresh failed', err);
    });
  }

  // Kick OFCL reload without blocking mesh tiles; markers appear when ready.
  refreshOfclStorms().catch(err => console.warn('[ofcl] refresh failed', err));

  if (deckInstance) refresh();
}

// Fetches the list of active forecast cycles, populates the dropdown, and
// switches to the most recent one. Falls back to the manifest already
// sitting at TILES_BASE_URL (see its declaration above) if the active-
// cycles endpoint isn't available, e.g. when testing the viewer standalone
// against a single adaptive_mesh_tile_chunker.py run.
async function initForecastCycles() {
  try {
    const res = await fetch(ACTIVE_CYCLES_URL);
    if (!res.ok) throw new Error(`${ACTIVE_CYCLES_URL} -> ${res.status}`);
    const cycles = sortCyclesDesc(await res.json());
    if (!cycles.length) throw new Error('tiles_active.json is empty');
    activeCyclesList = cycles;

    forecastCycleSelect.innerHTML = '';
    for (const cycle of cycles) {
      const opt = document.createElement('option');
      opt.value = cycle;
      opt.textContent = formatCycleLabel(cycle);
      forecastCycleSelect.appendChild(opt);
    }
    forecastCycleSelect.value = cycles[0];
    TILES_BASE_URL = `/chl_data/${cycles[0]}`;
  } catch (err) {
    console.warn('forecast cycle list unavailable, falling back to', TILES_BASE_URL, err);
    activeCyclesList = [];
    forecastCycleSelect.innerHTML = '';
    const opt = document.createElement('option');
    opt.value = '';
    opt.textContent = '(no forecast cycles found)';
    forecastCycleSelect.appendChild(opt);
  }

  forecastCycleSelect.addEventListener('change', e => {
    const cycle = e.target.value;
    if (!cycle) return;
    TILES_BASE_URL = `/chl_data/${cycle}`;
    loadManifestAndTiles().catch(err => {
      console.error('failed to load forecast cycle', cycle, err);
      setStatus(`ERROR loading cycle ${cycle}: ${err.message}`);
    });
  });
}

async function main() {
  wireScrollableSelects();
  for (const cmap of COLORMAPS) {
    const opt = document.createElement('option');
    opt.value = cmap.id;
    opt.textContent = cmap.label;
    if (cmap.id === DEFAULT_COLORMAP_ID) opt.selected = true;
    colormapSelect.appendChild(opt);
  }
  updateLegend();
  console.info(
    '[tile-diag] enabled (filter console by "tile-diag"). ' +
      'Disable: window.__tileDiag = false. Per-tile detail: window.__tileDiagVerbose = true'
  );
  await initStationCatalog();
  await initForecastCycles();
  await loadManifestAndTiles();

  const params = new URLSearchParams(window.location.search);
  currentViewState = {
    longitude: parseFloat(params.get('lon')) || 78.0,
    latitude: clampViewLatitude(parseFloat(params.get('lat')) || 10.5),
    zoom: parseFloat(params.get('zoom')) || 4.5,
    pitch: 0,
    bearing: 0
  };
  window.__debugNoBasemap = params.get('nobasemap') === '1';

  deckInstance = new Deck({
    parent: document.getElementById('map'),
    views: new PoleSafeGlobeView({
      controller: ZoomToCursorGlobeController,
      resolution: 4
    }),
    viewState: currentViewState,
    layers: [basemapLayerInstance],
    onViewStateChange: onDeckViewStateChange,
    onLoad: scheduleInitialRefresh,
    onClick: handleMapClick,
    pickingRadius: 12,
    getTooltip: ({object, layer}) => {
      if (!object) return null;
      if (layer?.id === 'ofcl-positions' || layer?.id === 'ofcl-tracks') {
        const pos = layer.id === 'ofcl-positions'
          ? object
          : (getOfclPositions().find(p => p.id === object.id) || null);
        if (!pos || pos.lon == null || pos.lat == null) return null;
        const label = pos.label || stormLabelFallback(pos);
        const nhcMs = nhcTenMinWindMs(pos.vmax);
        const tileWind = requestTileWindForStorm(pos);
        let tileLine;
        if (tileWind.status === 'pending') tileLine = 'Tile wind: loading…';
        else if (tileWind.status === 'no-mesh') tileLine = 'Tile wind: n/a (no mesh)';
        else if (tileWind.status === 'dry') tileLine = 'Tile wind: dry / missing';
        else if (tileWind.status === 'error') tileLine = 'Tile wind: unavailable';
        else tileLine = `Tile wind: ${formatMs(tileWind.value)}`;
        const nhcLine = `NHC 10-min wind: ${formatMs(nhcMs)}`
          + (pos.vmax != null
            ? ` <span style="color:#666">(${Math.round(pos.vmax)} kt → ×0.9)</span>`
            : '');
        return {
          html:
            `<div style="font:12px sans-serif;line-height:1.35;">` +
            `<strong>${label}</strong><br>${nhcLine}<br>${tileLine}</div>`,
          style: {
            background: 'rgba(255,255,255,0.96)',
            color: '#111',
            padding: '8px 10px',
            borderRadius: '4px'
          }
        };
      }
      if (layer?.id === 'stations') {
        return {
          html: `<div style="font:12px sans-serif;"><strong>${object.name}</strong><br>${object.stationId}</div>`,
          style: {background: 'rgba(255,255,255,0.95)', color: '#111', padding: '6px 8px', borderRadius: '4px'}
        };
      }
      return null;
    },
    // debug-only: lets preview_eval read back canvas pixels for diagnostics
    deviceProps: {createCanvasContext: {preserveDrawingBuffer: true}}
  });

  attachLayoutRefreshObserver();
  scheduleInitialRefresh();

  const aboutBtn = document.getElementById('aboutShadowWatchBtn');
  const aboutModal = document.getElementById('aboutModal');
  const aboutClose = document.getElementById('aboutModalClose');
  aboutBtn?.addEventListener('click', () => aboutModal?.classList.remove('hidden'));
  aboutClose?.addEventListener('click', () => aboutModal?.classList.add('hidden'));

  particleAnim = new ParticleAnimation({
    canvas: particleCanvas,
    deckInstance,
    store,
    getVisibleTiles: () => lastVisible,
    getField: () => activeVectorField,
    getTimestep: () => timestep
  });

  hydrographClose.addEventListener('click', () => {
    hydrographModal.classList.add('hidden');
    selectedStationId = null;
    ++hydrographRequestId;
    clearHydrographCsv();
  });
  hydrographDownloadCsv?.addEventListener('click', downloadHydrographCsv);

  timestepBigInput.addEventListener('input', e => setTimestep(parseInt(e.target.value, 10)));

  basemapSelect.addEventListener('change', e => {
    basemapStyle = e.target.value;
    basemapLayerInstance = createBasemapLayer();
    renderVisible();
  });

  displayFieldSelect.addEventListener('change', e => {
    displayField = e.target.value;
    resetLegendDomainInputs(); // a custom domain typed in for the old field shouldn't carry over
    // Per-field default colormap (Topo–Bathy for depth; Jet for everything else).
    const preferred = DISPLAY_FIELDS[displayField]?.defaultColormap || DEFAULT_COLORMAP_ID;
    if (getColormap(preferred)) {
      colormapId = preferred;
      if (colormapSelect) colormapSelect.value = preferred;
    }
    updateLegend();
    refresh(); // kicks off ensureSpeed for visible tiles if the new field needs it
  });

  colormapSelect.addEventListener('change', e => {
    colormapId = e.target.value;
    updateLegend();
    renderVisible();
  });

  opacitySlider.addEventListener('input', e => {
    const transparencyPct = parseInt(e.target.value, 10);
    dataOpacity = 1 - transparencyPct / 100;
    opacityValueEl.textContent = `${transparencyPct}%`;
    legendBarEl.style.opacity = String(dataOpacity);
    renderVisible();
  });

  legendMinInput.addEventListener('change', e => {
    const value = parseFloat(e.target.value);
    if (!Number.isFinite(value)) return;
    domainMin = value;
    updateLegend();
    renderVisible();
  });

  legendMaxInput.addEventListener('change', e => {
    const value = parseFloat(e.target.value);
    if (!Number.isFinite(value)) return;
    domainMax = value;
    updateLegend();
    renderVisible();
  });

  showEdgesToggle.addEventListener('change', e => {
    showEdges = e.target.checked;
    renderVisible();
  });

  vectorFieldSelect.addEventListener('change', e => {
    const value = e.target.value;
    activeVectorField = value === 'none' ? null : value;
    if (activeVectorField) {
      particleAnim.start();
      refresh(); // kicks off ensureVector for all currently visible tiles
    } else {
      particleAnim.stop();
    }
  });

  showDiagnosticsToggle.addEventListener('change', e => {
    statusEl.classList.toggle('hidden', !e.target.checked);
  });

  if (showOfclToggle) {
    showOfcl = showOfclToggle.checked;
    showOfclToggle.addEventListener('change', e => {
      showOfcl = e.target.checked;
      ofclTileWindCache.clear();
      if (stormHoverTipEl) {
        stormHoverTipEl.classList.add('hidden');
        delete stormHoverTipEl.dataset.stormKey;
      }
      // Always re-render mesh first so toggling NHC never blanks tiles.
      renderVisible();
      if (showOfcl && !ofclStorms.length) {
        refreshOfclStorms().catch(err => console.warn('[ofcl] refresh failed', err));
      } else if (!showOfcl) {
        ofclStorms = [];
      }
    });
  }

  initRenderAnimation({
    getManifest: () => manifest,
    getDeck: () => deckInstance,
    getDisplayFields: () => DISPLAY_FIELDS,
    getColormaps: () => COLORMAPS,
    getDefaultColormapId: () => DEFAULT_COLORMAP_ID,
    getTimesteps: () => manifest?.timesteps || [],
    getForecastCycle: () => forecastCycleSelect?.value || '',
    getTransparencyPct: () => Math.round((1 - dataOpacity) * 100),
    getLiveSettings: () => ({
      basemapStyle,
      colormapId,
      transparencyPct: Math.round((1 - dataOpacity) * 100),
      showEdges,
      showOfcl,
      displayField,
      domainMin,
      domainMax
    }),
    setTimestep,
    applyAnimSettings,
    snapshotViewerState,
    restoreViewerState,
    fitViewToBbox,
    waitVisibleReady,
    setUiChromeVisible,
    setShowStations,
    getLegendDrawInfo
  });

  initTrackParticles({
    getManifest: () => manifest,
    getDeck: () => deckInstance,
    getStore: () => store,
    getIndex: () => index,
    getTimesteps: () => manifest?.timesteps || [],
    getForecastCycle: () => forecastCycleSelect?.value || '',
    setTimestep,
    setTrackMarkers,
    setTrackResult,
    setTrackPlaceMode
  });

  anomalyApi = initAnomalyDetection({
    getManifest: () => manifest,
    getDeck: () => deckInstance,
    getActiveCycles: () => activeCyclesList.slice(),
    getTimesteps: () => manifest?.timesteps || [],
    getForecastCycle: () => forecastCycleSelect?.value || '',
    getTimestepIndex: () => timestep,
    getShowEdges: () => showEdges,
    setShowEdges: on => {
      showEdges = Boolean(on);
      if (showEdgesToggle) showEdgesToggle.checked = showEdges;
      renderVisible();
    },
    pickMeshNode: (lon, lat) => findMeshClickHit(lon, lat),
    setAnomalyMarkers,
    setAnomalySelectMode
  });
}

main().catch(err => {
  console.error(err);
  setStatus(`ERROR: ${err.message}`);
});
